import {
  ALL_MEMBERSHIP_PLANS,
  MARKETED_MEMBERSHIP_PLAN_TYPES,
  type AllMembershipPlanType,
  type ApiHook,
  type MarketedMembershipPlanType,
  type MembershipPlanShape,
  type RetrievePlanTiersArg,
} from '@frontend/api-client';
import { sortByNumber } from '@frontend/duck-tape';
import { useMemo } from 'react';

const BACKEND_CODE_TO_FRONTEND_KEY: Record<string, string> = {
  A_LA_CARTE: 'aLaCarte',
  ESSENTIAL: 'essentials',
  HOUSEHOLD: 'household',
  HOUSEHOLD_PLUS: 'householdPlus',
  INDIVIDUAL: 'core',
  STARTER: 'starter',
  TRIAL: 'trial',
  VIP: 'vip',
};

const convertBackendCodeToFrontendKey = (code: string): string => {
  return BACKEND_CODE_TO_FRONTEND_KEY[code] ?? code;
};

type UseRetrievePlanTiersQueryHook = ApiHook<'useRetrievePlanTiersQuery'>;

type Dependencies = {
  isHouseholdPlusActive: boolean;
  shouldUseBackendPlans: boolean;
  useRetrievePlanTiersQuery: UseRetrievePlanTiersQueryHook;
};

export type UseMembershipPlansOptions = {
  includeLinkedInPlans?: boolean;
  promoCode?: string;
};

export type UseMembershipPlansReturn = {
  hasCurrentPlanData: boolean;
  isError: boolean;
  isFetching: boolean;
  isLoading: boolean;
  planLookup: Partial<Record<AllMembershipPlanType, MembershipPlanShape>>;
  refetch: () => void;
  selectablePlans: Array<MembershipPlanShape & { planType: MarketedMembershipPlanType }>;
};

const sortByPriceAscending = sortByNumber<MembershipPlanShape & { planType: MarketedMembershipPlanType }>({
  getter: (plan) => Number(plan.price),
  sortDirection: 'asc',
});

export const useMembershipPlans = (
  { includeLinkedInPlans = false, promoCode }: UseMembershipPlansOptions = {},
  { isHouseholdPlusActive, shouldUseBackendPlans, useRetrievePlanTiersQuery }: Dependencies,
): UseMembershipPlansReturn => {
  const queryArg: RetrievePlanTiersArg = promoCode ? { promoCode } : undefined;
  const { currentData, isError, isFetching, isLoading, refetch } = useRetrievePlanTiersQuery(queryArg, {
    skip: !shouldUseBackendPlans,
  });
  const plans = useMemo(() => currentData ?? [], [currentData]);
  const hasCurrentPlanData = !shouldUseBackendPlans || typeof currentData !== 'undefined';

  const planLookup = useMemo(() => {
    if (!shouldUseBackendPlans) {
      return ALL_MEMBERSHIP_PLANS;
    }

    return plans.reduce<Record<string, (typeof plans)[0]>>((acc, plan) => {
      const frontendKey = convertBackendCodeToFrontendKey(plan.planCode);
      return {
        ...acc,
        [plan.planCode]: plan,
        [frontendKey]: plan,
      };
    }, {});
  }, [plans, shouldUseBackendPlans]);

  const selectablePlans = useMemo(() => {
    if (!shouldUseBackendPlans) {
      const planTypes = [
        ...MARKETED_MEMBERSHIP_PLAN_TYPES,
        ...(includeLinkedInPlans ? (['starter'] as const) : []),
        ...(isHouseholdPlusActive ? (['householdPlus'] as const) : []),
      ];

      return sortByPriceAscending(planTypes.map((planType) => ({ ...ALL_MEMBERSHIP_PLANS[planType], planType })));
    }

    const selectableBackendPlans = plans
      .filter((plan) => plan.isSelectable)
      .filter((plan) => isHouseholdPlusActive || plan.planCode !== 'HOUSEHOLD_PLUS')
      .filter((plan) => includeLinkedInPlans || plan.planCode !== 'STARTER')
      .map((plan) => ({
        ...plan,
        planType: convertBackendCodeToFrontendKey(plan.planCode) as MarketedMembershipPlanType,
      }));

    return sortByPriceAscending(selectableBackendPlans);
  }, [plans, shouldUseBackendPlans, isHouseholdPlusActive, includeLinkedInPlans]);

  return {
    hasCurrentPlanData,
    isError,
    isFetching,
    isLoading,
    planLookup,
    refetch,
    selectablePlans,
  };
};
